import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Search, MapPin, Briefcase, GraduationCap } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const counties = ["All", "Sonoma", "Napa", "Marin", "Lake", "Mendocino"];

export default function CommunityPage({ user }) {
  const [graduates, setGraduates] = useState([]);
  const [filteredGraduates, setFilteredGraduates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCounty, setSelectedCounty] = useState("All");
  const [selectedCertification, setSelectedCertification] = useState("All");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Get all unique certifications from graduates
  const [allCertifications, setAllCertifications] = useState([]);

  useEffect(() => {
    fetchGraduates();
  }, []);

  useEffect(() => {
    // Extract unique certifications
    const certs = new Set();
    graduates.forEach(grad => {
      if (grad.certifications) {
        grad.certifications.forEach(cert => certs.add(cert));
      }
    });
    setAllCertifications(["All", ...Array.from(certs).sort()]);
  }, [graduates]);

  useEffect(() => {
    filterGraduates();
  }, [graduates, searchTerm, selectedCounty, selectedCertification]);

  const fetchGraduates = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users/graduates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGraduates(response.data);
    } catch (error) {
      console.error("Failed to fetch graduates", error);
    } finally {
      setLoading(false);
    }
  };

  const filterGraduates = () => {
    let filtered = graduates;

    if (searchTerm) {
      filtered = filtered.filter(
        (grad) =>
          grad.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (grad.trade_focus && grad.trade_focus.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (selectedCounty !== "All") {
      filtered = filtered.filter((grad) => grad.county === selectedCounty);
    }

    if (selectedCertification !== "All") {
      filtered = filtered.filter((grad) => 
        grad.certifications && grad.certifications.includes(selectedCertification)
      );
    }

    setFilteredGraduates(filtered);
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <div className="space-y-6" data-testid="community-page">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk', color: '#4ECDC4' }}>
          Graduate Directory
        </h1>
        <p className="text-gray-600">Connect with fellow NextGen Trades alumni</p>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search by name or trade focus..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="graduate-search-input"
          />
        </div>
        <Select value={selectedCounty} onValueChange={setSelectedCounty}>
          <SelectTrigger data-testid="graduate-county-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {counties.map((c) => (
              <SelectItem key={c} value={c}>{c} County</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedCertification} onValueChange={setSelectedCertification}>
          <SelectTrigger data-testid="graduate-certification-filter">
            <SelectValue placeholder="Filter by Certification" />
          </SelectTrigger>
          <SelectContent>
            {allCertifications.map((cert) => (
              <SelectItem key={cert} value={cert}>
                {cert === "All" ? "All Certifications" : cert}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading graduates...</div>
      ) : filteredGraduates.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <GraduationCap className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600">No graduates found matching your criteria</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredGraduates.map((grad) => (
            <Card
              key={grad.id}
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4"
              style={{ borderLeftColor: '#FF6B6B' }}
              onClick={() => navigate(`/profile/${grad.id}`)}
              data-testid={`graduate-card-${grad.id}`}
            >
              <CardHeader>
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16" style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)' }}>
                    <AvatarFallback className="text-white text-lg font-bold">
                      {getInitials(grad.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-1" style={{ fontFamily: 'Space Grotesk' }}>
                      {grad.name}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {grad.trade_focus}
                    </CardDescription>
                  </div>
                  {grad.grad_year && (
                    <Badge variant="outline" className="text-sm">Class of {grad.grad_year}</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {grad.county && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{grad.county} County</span>
                    </div>
                  )}
                  {grad.bio && (
                    <p className="text-sm text-gray-600 line-clamp-2">{grad.bio}</p>
                  )}
                  {grad.contact_preference && (
                    <div className="flex items-center gap-2 text-sm" style={{ color: '#4ECDC4' }}>
                      <Briefcase className="h-4 w-4" />
                      <span>{grad.contact_preference}</span>
                    </div>
                  )}
                  {grad.certifications && grad.certifications.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {grad.certifications.slice(0, 3).map((cert) => (
                        <Badge key={cert} variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 text-xs">
                          {cert}
                        </Badge>
                      ))}
                      {grad.certifications.length > 3 && (
                        <Badge variant="outline" className="text-xs text-gray-600">
                          +{grad.certifications.length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}